import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationStore, UserNotification } from '@/store/notification.store';
import { escalationService } from '@/services/escalation.service';
import { safeInternalPath } from '@/utils/safe-url';

import Button, { IconButton } from '../common/Button';
import FaIcon from '../common/FaIcon';

interface NotificationItemProps {
  notification: UserNotification;
  onMarkAsRead: (id: string) => void;
  onDismiss: (id: string) => void;
}

function getTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

function getPriorityColor(priority: string): string {
  switch (priority) {
    case 'urgent':
      return 'bg-red-500';
    case 'high':
      return 'bg-orange-500';
    case 'normal':
      return 'bg-primary-500';
    case 'low':
      return 'bg-gray-400';
    default:
      return 'bg-primary-500';
  }
}

function getPriorityLabel(priority: string): string | null {
  switch (priority) {
    case 'urgent':
      return 'URGENT';
    case 'high':
      return 'HIGH';
    default:
      return null;
  }
}

function getPriorityBadgeClass(priority: string): string {
  switch (priority) {
    case 'urgent':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    case 'high':
      return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
    default:
      return '';
  }
}

function getNotificationRoute(notification: UserNotification): string | null {
  // action_url is server-controlled — only allow app-internal relative routes
  // for router navigation; reject absolute/dangerous URLs.
  if (notification.action_url) return safeInternalPath(notification.action_url);
  if (!notification.resource_type || !notification.resource_id) return null;

  switch (notification.resource_type) {
    case 'supply_request':
      return `/supply-requests/${notification.resource_id}`;
    case 'task':
      return `/tasks/${notification.resource_id}`;
    case 'incident':
      return `/incidents/${notification.resource_id}`;
    case 'shelter':
      return `/shelters/${notification.resource_id}`;
    case 'damage_assessment':
      return `/damage-assessments/${notification.resource_id}`;
    case 'ics_form':
    case 'form':
      return `/forms/${notification.resource_id}`;
    case 'weather_alert':
      return '/weather';
    default:
      return null;
  }
}

export function NotificationItem({
  notification,
  onMarkAsRead,
  onDismiss,
}: NotificationItemProps) {
  const navigate = useNavigate();
  const { closePanel } = useNotificationStore();
  const isUnread = notification.status === 'unread';
  const [acknowledging, setAcknowledging] = useState(false);
  const [isAcknowledged, setIsAcknowledged] = useState(
    notification.acknowledged === true
  );
  const [ackError, setAckError] = useState(false);

  const requiresAck = notification.requires_acknowledgement === true && !isAcknowledged;
  const escalationLevel = notification.escalation_level || 0;
  const priorityLabel = getPriorityLabel(notification.priority);

  const handleClick = () => {
    if (isUnread) {
      onMarkAsRead(notification.id);
    }

    const route = getNotificationRoute(notification);
    if (route) {
      closePanel();
      navigate(route);
    }
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDismiss(notification.id);
  };

  const handleAcknowledge = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (acknowledging || isAcknowledged) return;

    setAcknowledging(true);
    try {
      const autoAction = notification.resource_type === 'task' ? 'task_in_progress' : undefined;
      await escalationService.acknowledgeNotification(notification.id, {
        auto_action: autoAction,
      });
      setIsAcknowledged(true);
      if (isUnread) {
        onMarkAsRead(notification.id);
      }
    } catch {
      setAckError(true);
      setTimeout(() => setAckError(false), 5000);
    } finally {
      setAcknowledging(false);
    }
  };

  return (
    <div
      className={`
        relative flex items-start gap-3 px-4 py-3.5
        border-b border-theme-primary
        transition-colors
        ${isUnread ? 'bg-primary-50 hover:bg-primary-100 dark:bg-gray-700 dark:hover:bg-primary-900/30' : 'bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700/50'}
      `}
    >
      {/* Clickable area: priority indicator + content */}
      <div onClick={handleClick} className="flex items-start gap-3 flex-1 min-w-0 cursor-pointer">
        {/* Priority indicator */}
        <div
          className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${getPriorityColor(
            notification.priority
          )}`}
        />

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-wrap items-center gap-1.5 min-w-0">
              <p
                className={`text-[15px] leading-snug ${
                  isUnread
                    ? 'font-semibold text-theme-primary'
                    : 'font-medium text-gray-800 dark:text-gray-200'
                }`}
              >
                {notification.title}
              </p>
              {priorityLabel && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${getPriorityBadgeClass(notification.priority)}`}>
                  {priorityLabel}
                </span>
              )}
              {escalationLevel > 0 && (
                <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 flex-shrink-0">
                  L{escalationLevel}
                </span>
              )}
            </div>
            <span className="text-xs text-theme-tertiary flex-shrink-0 mt-0.5">
              {getTimeAgo(notification.created_at)}
            </span>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-3 leading-relaxed">
            {notification.body}
          </p>

          {/* Acknowledge button */}
          {requiresAck && (
            <Button
              variant="primary"
              size="sm"
              icon={<FaIcon icon="check" className="w-4 h-4" />}
              onClick={handleAcknowledge}
              disabled={acknowledging}
              loading={acknowledging}
              className="mt-2"
            >
              {acknowledging ? 'Acknowledging...' : 'Acknowledge'}
            </Button>
          )}

          {isAcknowledged && (
            <span className="mt-1.5 inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
              <FaIcon icon="check" className="w-4 h-4" />
              Acknowledged
            </span>
          )}

          {ackError && (
            <div className="mt-1.5 flex items-center gap-2">
              <span className="text-xs text-red-600 dark:text-red-400">
                Failed to acknowledge.
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAcknowledge}
                className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium"
              >
                Retry
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Dismiss button - outside clickable area so it won't trigger navigation/close */}
      <IconButton
        icon={<FaIcon icon="xmark" className="w-4 h-4" />}
        label="Dismiss notification"
        variant="ghost"
        size="sm"
        onClick={handleDismiss}
        className="flex-shrink-0"
      />

      {/* Unread indicator dot */}
      {isUnread && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-primary-500 rounded-r" />
      )}
    </div>
  );
}
